import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import WalletGauge from '../../components/wallet/WalletGauge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Wallet as WalletIcon,
  Plus,
  CreditCard,
  Building2,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  Lock,
  Folder,
  AlertTriangle,
  Gift,
  Sparkles,
  CheckCircle2,
  TrendingDown
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useSearchParams, useNavigate } from 'react-router-dom';

const WalletPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTopup, setShowTopup] = useState(false);
  const [showGiftCode, setShowGiftCode] = useState(false);
  const [topupHours, setTopupHours] = useState('10');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [processing, setProcessing] = useState(false);
  const [giftCode, setGiftCode] = useState('');
  const [redeemingCode, setRedeemingCode] = useState(false);
  const [codeRedeemed, setCodeRedeemed] = useState(null);
  const [rateTiers, setRateTiers] = useState([]);

  useEffect(() => {
    fetchWallet();
    fetchRateTiers();
    
    // Check for payment success
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchWallet = async () => {
    try {
      const response = await api.get('/wallet/');
      setWallet(response.data);
    } catch (error) {
      console.error('Error fetching wallet:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRateTiers = async () => {
    try {
      const res = await api.get('/settings/hourly-rates/public');
      if (res.data?.tiers && res.data.tiers.length > 0) {
        setRateTiers(res.data.tiers);
      }
    } catch (error) {
      // Use default rate from wallet
    }
  };

  // Calculate price with progressive rates and TVA
  const calculateProgressivePrice = (hours) => {
    if (!rateTiers.length) {
      // Fallback to fixed rate
      const totalHT = hours * hourlyRate;
      const tvaAmount = totalHT * 0.20;
      const totalTTC = totalHT + tvaAmount;
      return { totalHT, tvaAmount, totalTTC, avgRate: hourlyRate, savings: 0, total: totalHT };
    }

    let totalPrice = 0;
    let remainingHours = hours;

    for (let i = 0; i < rateTiers.length && remainingHours > 0; i++) {
      const tier = rateTiers[i];
      const tierMin = tier.min_hours || 0;
      const tierMax = tier.max_hours || Infinity;
      const tierRange = tierMax - tierMin;
      const hoursInTier = Math.min(remainingHours, tierRange);
      
      if (hoursInTier > 0) {
        totalPrice += hoursInTier * tier.rate;
        remainingHours -= hoursInTier;
      }
    }

    const basePrice = hours * (rateTiers[0]?.rate || hourlyRate);
    const savings = basePrice - totalPrice;
    const avgRate = hours > 0 ? totalPrice / hours : (rateTiers[0]?.rate || hourlyRate);
    
    // Calculate TVA (20%)
    const totalHT = totalPrice;
    const tvaAmount = totalHT * 0.20;
    const totalTTC = totalHT + tvaAmount;
    
    return { 
      totalHT: Math.round(totalHT * 100) / 100, 
      tvaAmount: Math.round(tvaAmount * 100) / 100, 
      totalTTC: Math.round(totalTTC * 100) / 100, 
      avgRate: Math.round(avgRate * 100) / 100, 
      savings,
      // Keep backwards compatibility
      total: totalHT
    };
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Paiement réussi ! Vos heures ont été ajoutées.');
        fetchWallet();
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const [bankDetails, setBankDetails] = useState(null);
  const [transferReference, setTransferReference] = useState('');
  const [bankDetailsError, setBankDetailsError] = useState(false);

  const handleTopup = async () => {
    const hours = parseFloat(topupHours);
    if (!hours || hours <= 0) {
      toast.error('Veuillez entrer un nombre d\'heures valide');
      return;
    }
    
    setProcessing(true);
    setBankDetailsError(false);
    
    try {
      const response = await api.post('/wallet/topup', {
        hours,
        payment_method: paymentMethod
      });
      
      if (paymentMethod === 'stripe') {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        // Bank transfer - show bank details
        const details = response.data.bank_details;
        
        // Check if bank details are properly configured
        if (!details || (!details.iban && !details.bic)) {
          setBankDetailsError(true);
          toast.error('Les coordonnées bancaires de l\'agence ne sont pas configurées. Contactez l\'agence.');
          setProcessing(false);
          return;
        }
        
        setBankDetails(details);
        setTransferReference(response.data.reference);
        toast.success('Demande de virement enregistrée');
        fetchWallet();
        setProcessing(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la demande');
      setProcessing(false);
    }
  };

  const handleRedeemGiftCode = async () => {
    if (!giftCode.trim()) {
      toast.error('Veuillez entrer un code cadeau');
      return;
    }
    
    setRedeemingCode(true);
    try {
      const response = await api.post('/wallet/redeem-gift-code', { code: giftCode.trim() });
      setCodeRedeemed({
        hours: response.data.hours_credited,
        amount: response.data.amount_credited,
        newBalance: response.data.new_balance
      });
      toast.success(response.data.message);
      fetchWallet();
      setGiftCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide');
    } finally {
      setRedeemingCode(false);
    }
  };

  const hourlyRate = wallet?.hourly_rate || 75;
  const presetAmounts = [5, 10, 20, 50];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1a365d]" data-testid="wallet-title">
              Mon Wallet
            </h1>
            <p className="text-sm md:text-base text-[#64748b] mt-1">
              Gérez votre crédit d&apos;heures
            </p>
          </div>
          
          <div className="flex gap-2 md:gap-3">
            {/* Gift Code Dialog */}
            <Dialog open={showGiftCode} onOpenChange={(open) => { setShowGiftCode(open); if (!open) setCodeRedeemed(null); }}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="border-purple-300 text-purple-600 hover:bg-purple-50 text-xs md:text-sm" data-testid="gift-code-btn">
                  <Gift className="w-4 h-4 mr-1 md:mr-2" />
                  <span className="hidden sm:inline">Code cadeau</span>
                  <span className="sm:hidden">Code</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-purple-600" />
                    Utiliser un code cadeau
                  </DialogTitle>
                </DialogHeader>
                
                {codeRedeemed ? (
                  <div className="py-8 text-center space-y-4">
                    <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-green-600">Code appliqué !</h3>
                      <p className="text-slate-600 mt-2">
                        <span className="font-semibold text-2xl">+{codeRedeemed.hours.toFixed(1)}h</span>
                        <span className="text-sm ml-2">({codeRedeemed.amount.toLocaleString('fr-FR')}€)</span>
                      </p>
                      <p className="text-sm text-slate-500 mt-2">
                        Nouveau solde : <span className="font-semibold">{codeRedeemed.newBalance.toFixed(1)}h</span>
                      </p>
                    </div>
                    <Button onClick={() => { setShowGiftCode(false); setCodeRedeemed(null); }} className="btn-primary">
                      Fermer
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6 py-4">
                    <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-5 h-5 text-purple-500" />
                        <span className="font-medium text-purple-700">Vous avez un code cadeau ?</span>
                      </div>
                      <p className="text-sm text-purple-600">
                        Entrez-le ci-dessous pour créditer votre wallet instantanément !
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Code cadeau</Label>
                      <Input
                        value={giftCode}
                        onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                        placeholder="Entrez votre code (ex: GIFT-ABC123)"
                        className="text-center font-mono text-lg uppercase tracking-wider"
                        data-testid="gift-code-input"
                      />
                    </div>
                    
                    <Button 
                      onClick={handleRedeemGiftCode}
                      className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                      disabled={redeemingCode || !giftCode.trim()}
                      data-testid="redeem-code-btn"
                    >
                      {redeemingCode ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Vérification...
                        </>
                      ) : (
                        <>
                          <Gift className="w-4 h-4 mr-2" />
                          Valider le code
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Topup Dialog */}
            <Dialog open={showTopup} onOpenChange={setShowTopup}>
              <DialogTrigger asChild>
                <Button className="btn-primary text-xs md:text-sm" size="sm" data-testid="topup-btn">
                  <Plus className="w-4 h-4 mr-1 md:mr-2" />
                  Recharger
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Recharger mon wallet</DialogTitle>
                </DialogHeader>
              
                <div className="space-y-6 py-4">
                  {/* Quick amounts */}
                  <div className="space-y-2">
                    <Label>Heures à créditer</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {presetAmounts.map((amount) => (
                        <Button
                          key={amount}
                          type="button"
                          variant={topupHours === String(amount) ? 'default' : 'outline'}
                          className={cn(
                            topupHours === String(amount) && 'bg-[#00d4aa] text-[#1a365d] hover:bg-[#00b38f]'
                          )}
                          onClick={() => setTopupHours(String(amount))}
                        >
                          {amount}h
                        </Button>
                      ))}
                    </div>
                    <Input
                      type="number"
                      value={topupHours}
                      onChange={(e) => setTopupHours(e.target.value)}
                      placeholder="Nombre d'heures"
                      className="input-default mt-2"
                      data-testid="topup-hours-input"
                    />
                  </div>
                
                  {/* Amount preview with progressive pricing */}
                  <div className="p-4 bg-[#f8fafc] rounded-xl">
                    {(() => {
                      const hours = parseFloat(topupHours || 0);
                      const priceInfo = calculateProgressivePrice(hours);
                      return (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-[#64748b]">Montant total</span>
                            <span className="font-mono text-2xl font-bold text-[#1a365d]">
                              {priceInfo.total.toLocaleString('fr-FR')}€
                            </span>
                          </div>
                          <p className="text-xs text-[#64748b] mt-1">
                            Taux moyen: {priceInfo.avgRate.toLocaleString('fr-FR')}€/h
                            {rateTiers.length > 0 && ' (tarif dégressif)'}
                          </p>
                          
                          {/* Prix détaillé avec TVA */}
                          <div className="mt-3 p-3 bg-slate-50 rounded-lg space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Montant HT</span>
                              <span className="font-medium">{priceInfo.totalHT?.toLocaleString('fr-FR')}€</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">TVA 20%</span>
                              <span className="font-medium">{priceInfo.tvaAmount?.toLocaleString('fr-FR')}€</span>
                            </div>
                            <div className="flex justify-between text-base font-bold border-t pt-2" style={{ color: theme.colors?.primary }}>
                              <span>Total TTC</span>
                              <span>{priceInfo.totalTTC?.toLocaleString('fr-FR')}€</span>
                            </div>
                          </div>
                          
                          {priceInfo.savings > 0 && (
                            <div className="mt-2 p-2 bg-emerald-50 rounded-lg">
                              <p className="text-xs text-emerald-700 font-medium flex items-center gap-1">
                                <TrendingDown className="w-3 h-3" />
                                Économie: {priceInfo.savings.toLocaleString('fr-FR')}€ grâce au tarif dégressif
                              </p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                
                  {/* Payment method */}
                  <div className="space-y-2">
                    <Label>Mode de paiement</Label>
                    <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                      <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-[#f8fafc] cursor-pointer">
                        <RadioGroupItem value="stripe" id="stripe" />
                        <Label htmlFor="stripe" className="flex items-center gap-2 cursor-pointer flex-1">
                          <CreditCard className="w-5 h-5 text-[#6366f1]" />
                          <div>
                            <p className="font-medium">Carte bancaire</p>
                            <p className="text-xs text-[#64748b]">Paiement sécurisé par Stripe</p>
                          </div>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-[#f8fafc] cursor-pointer">
                        <RadioGroupItem value="bank_transfer" id="bank_transfer" />
                        <Label htmlFor="bank_transfer" className="flex items-center gap-2 cursor-pointer flex-1">
                          <Building2 className="w-5 h-5 text-[#1a365d]" />
                          <div>
                            <p className="font-medium">Virement bancaire</p>
                            <p className="text-xs text-[#64748b]">Voir les coordonnées bancaires de l&apos;agence</p>
                          </div>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                
                  {/* Error when bank details not configured */}
                  {bankDetailsError && paymentMethod === 'bank_transfer' && (
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200 space-y-3">
                      <div className="flex items-center gap-2 text-red-700 font-semibold">
                        <AlertTriangle className="w-5 h-5" />
                        Paiement par virement non disponible
                      </div>
                      <p className="text-sm text-red-600">
                        Les coordonnées bancaires de l&apos;agence ne sont pas encore configurées. 
                        Veuillez contacter l&apos;agence ou choisir un autre mode de paiement.
                      </p>
                      <Button 
                        variant="outline" 
                        className="w-full mt-2"
                        onClick={() => {
                          setBankDetailsError(false);
                          setPaymentMethod('stripe');
                        }}
                      >
                        Payer par carte bancaire
                      </Button>
                    </div>
                  )}

                  {/* Bank details display after request */}
                  {bankDetails && paymentMethod === 'bank_transfer' && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                      <div className="flex items-center gap-2 text-blue-700 font-semibold">
                        <Building2 className="w-5 h-5" />
                        Coordonnées bancaires (RIB)
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        {bankDetails.holder && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Titulaire :</span>
                            <span className="font-medium">{bankDetails.holder}</span>
                          </div>
                        )}
                        {bankDetails.bank_name && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Banque :</span>
                            <span className="font-medium">{bankDetails.bank_name}</span>
                          </div>
                        )}
                        {bankDetails.iban && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">IBAN :</span>
                            <span className="font-mono font-medium text-xs">{bankDetails.iban}</span>
                          </div>
                        )}
                        {bankDetails.bic && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">BIC :</span>
                            <span className="font-mono font-medium">{bankDetails.bic}</span>
                          </div>
                        )}
                      </div>
                      
                      {transferReference && (
                        <div className="mt-3 p-3 bg-amber-50 rounded border border-amber-200">
                          <p className="text-xs text-amber-800">
                            <strong>⚠️ Important :</strong> Indiquez cette référence dans le motif de votre virement :
                          </p>
                          <p className="text-lg font-bold text-amber-900 mt-1">
                            Wallet MSC - {user?.company_name || `${user?.first_name} ${user?.last_name}`}
                          </p>
                        </div>
                      )}
                      
                      <div className="mt-3 p-2 bg-slate-100 rounded text-xs text-slate-600">
                        <p>📋 Votre demande a été enregistrée. Une fois le virement reçu et validé par l&apos;agence, votre wallet sera crédité automatiquement.</p>
                      </div>
                      
                      <Button 
                        variant="outline" 
                        className="w-full mt-2"
                        onClick={() => {
                          setBankDetails(null);
                          setTransferReference('');
                          setShowTopup(false);
                        }}
                      >
                        Fermer
                      </Button>
                    </div>
                  )}
                
                  {!bankDetails && !bankDetailsError && (
                    <Button 
                      onClick={handleTopup}
                      className="btn-primary w-full h-12"
                      disabled={processing}
                      data-testid="confirm-topup-btn"
                    >
                      {processing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Traitement...
                        </>
                      ) : paymentMethod === 'stripe' ? (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Payer {calculateProgressivePrice(parseFloat(topupHours || 0)).totalTTC?.toLocaleString('fr-FR')}€ TTC
                        </>
                      ) : (
                        <>
                          <Building2 className="w-4 h-4 mr-2" />
                          Recharger par virement
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner" />
            </div>
          ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Wallet Balance */}
            <Card className="card lg:col-span-1" data-testid="wallet-balance-card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1a365d] flex items-center gap-2">
                  <WalletIcon className="w-5 h-5" />
                  Solde actuel
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center py-6">
                <WalletGauge 
                  balance={wallet?.balance_hours || 0}
                  reservedHours={wallet?.reserved_hours || 0}
                  maxBalance={100}
                  hourlyRate={hourlyRate}
                  reservations={wallet?.reservations || []}
                  showDetails={false}
                />
                
                {/* Summary below gauge */}
                <div className="w-full mt-4 space-y-2 text-sm">
                  <div className="flex justify-between p-2 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Total crédit</span>
                    <span className="font-semibold">{(wallet?.balance_hours || 0).toFixed(1)}h</span>
                  </div>
                  {(wallet?.reserved_hours || 0) > 0 && (
                    <>
                      <div className="flex justify-between p-2 bg-slate-100 rounded-lg">
                        <span className="text-slate-600 flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          Réservé (projets)
                        </span>
                        <span className="font-semibold text-slate-500">{(wallet?.reserved_hours || 0).toFixed(1)}h</span>
                      </div>
                      <div className={cn(
                        "flex justify-between p-2 rounded-lg border",
                        (wallet?.available_hours || 0) >= 0 
                          ? "bg-green-50 border-green-200" 
                          : "bg-red-50 border-red-200"
                      )}>
                        <span className={cn(
                          "font-medium",
                          (wallet?.available_hours || 0) >= 0 ? "text-green-700" : "text-red-700"
                        )}>
                          {(wallet?.available_hours || 0) >= 0 ? "Disponible" : "Solde négatif"}
                        </span>
                        <span className={cn(
                          "font-bold",
                          (wallet?.available_hours || 0) >= 0 ? "text-green-600" : "text-red-600"
                        )}>
                          {(wallet?.available_hours || 0).toFixed(1)}h
                        </span>
                      </div>
                    </>
                  )}
                  
                  {/* Alert for negative balance */}
                  {(wallet?.available_hours || 0) < 0 && (
                    <div className="mt-4 p-4 bg-red-100 border border-red-300 rounded-xl">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-semibold text-red-800">Solde insuffisant</p>
                          <p className="text-sm text-red-700 mt-1">
                            Vos réservations dépassent votre crédit de <strong>{Math.abs(wallet?.available_hours || 0).toFixed(1)}h</strong> 
                            ({(Math.abs(wallet?.available_hours || 0) * hourlyRate).toLocaleString('fr-FR')}€).
                          </p>
                          <p className="text-sm text-red-700 mt-1">
                            Veuillez régulariser votre compte pour continuer à utiliser nos services.
                          </p>
                          <Button 
                            onClick={() => {
                              setTopupHours(String(Math.ceil(Math.abs(wallet?.available_hours || 0))));
                              setShowTopup(true);
                            }}
                            className="mt-3 bg-red-600 hover:bg-red-700 text-white"
                            size="sm"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Régulariser maintenant (+{Math.ceil(Math.abs(wallet?.available_hours || 0))}h)
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Reservations - Show if any */}
            {(wallet?.reservations?.length > 0) && (
              <Card className="card lg:col-span-2" data-testid="reservations-card">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-[#1a365d] flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    Budget réservé pour vos projets
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500 mb-4">
                    Ces heures sont bloquées pour vos projets en cours. Elles seront déduites au fur et à mesure de l&apos;avancement.
                  </p>
                  <div className="space-y-3">
                    {wallet.reservations.map((reservation, index) => (
                      <div 
                        key={reservation.id || index}
                        className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50 hover:border-blue-300 hover:bg-blue-50/50 transition-all cursor-pointer"
                        onClick={() => navigate(`/projects/${reservation.project_id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                            <Folder className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium text-[#1a365d] hover:text-blue-600 transition-colors">
                              {reservation.project_name || 'Projet'}
                              <span className="ml-2 text-xs text-blue-500">→</span>
                            </p>
                            <p className="text-xs text-slate-500">
                              Réservé le {new Date(reservation.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold text-slate-600">
                            {reservation.reserved_hours}h
                          </p>
                          <p className="text-xs text-slate-500">
                            {(reservation.reserved_amount || reservation.reserved_hours * hourlyRate).toLocaleString('fr-FR')}€
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Transactions */}
            <Card className={cn("card", wallet?.reservations?.length > 0 ? "lg:col-span-3" : "lg:col-span-2")} data-testid="transactions-card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1a365d]">
                  Historique des transactions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {wallet?.transactions?.length > 0 ? (
                    wallet.transactions.map((transaction, index) => (
                      <div 
                        key={transaction.id || index}
                        className="flex items-center justify-between p-4 rounded-xl border border-[#e2e8f0]"
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center",
                            transaction.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                          )}>
                            {transaction.type === 'credit' ? (
                              <ArrowDownRight className="w-5 h-5 text-green-600" />
                            ) : (
                              <ArrowUpRight className="w-5 h-5 text-red-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-[#1a365d]">
                              {transaction.description}
                            </p>
                            <p className="text-xs text-[#64748b]">
                              {new Date(transaction.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={cn(
                            "font-mono font-bold",
                            transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                          )}>
                            {transaction.type === 'credit' ? '+' : '-'}{transaction.hours}h
                          </p>
                          <p className="text-xs text-[#64748b]">
                            {transaction.amount?.toLocaleString('fr-FR')}€
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12 text-[#64748b]">
                      <WalletIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Aucune transaction pour le moment</p>
                      <Button 
                        onClick={() => setShowTopup(true)}
                        className="btn-primary mt-4"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Effectuer mon premier rechargement
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info Card */}
        <Card className="card mt-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#00d4aa]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <WalletIcon className="w-6 h-6 text-[#00d4aa]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#1a365d] mb-2">
                  Comment fonctionne le wallet ?
                </h3>
                <p className="text-sm text-[#64748b]">
                  Votre wallet vous permet de provisionner des heures de communication valables pour tous les services de l&apos;agence MSC.
                  Rechargez votre compte en heures et utilisez-les sur vos projets. Le taux horaire actuel est de <strong>{hourlyRate}€/h</strong>.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default WalletPage;
